import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
  /** Desabilita os botões e mostra o spinner no de confirmar — pra mutations em andamento. */
  confirmando?: boolean;
}

const ESTILO_POR_TIPO: Record<
  NonNullable<ConfirmDialogProps['type']>,
  { icone: string; iconeFundo: string; botaoConfirmar: 'danger' | 'primary' }
> = {
  danger: { icone: 'text-red-600', iconeFundo: 'bg-red-100', botaoConfirmar: 'danger' },
  warning: { icone: 'text-amber-600', iconeFundo: 'bg-amber-100', botaoConfirmar: 'primary' },
  info: { icone: 'text-indigo-600', iconeFundo: 'bg-indigo-100', botaoConfirmar: 'primary' },
};

/**
 * Confirmação genérica (ícone + título + mensagem + Cancelar/Confirmar) — pra qualquer
 * ação que precise de "tem certeza?" com mais contexto/estilo do que o `window.confirm()`
 * nativo do navegador (só texto puro, sem explicação nem visual). Baseado no ConfirmDialog
 * já usado em Projects/ObjectivesPhase, promovido pra cá (`components/shared`) pra virar o
 * padrão reutilizável entre módulos, em vez de cada um reimplementar o próprio.
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  type = 'warning',
  confirmando = false,
}: ConfirmDialogProps) {
  if (!isOpen) return null;
  const estilo = ESTILO_POR_TIPO[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={confirmando ? undefined : onCancel} />
      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <button
          type="button"
          onClick={onCancel}
          disabled={confirmando}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="p-6">
          <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${estilo.iconeFundo}`}>
            <ExclamationTriangleIcon className={`h-6 w-6 ${estilo.icone}`} />
          </div>

          <div className="mb-6">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
            <p className="leading-relaxed text-gray-600">{message}</p>
          </div>

          <div className="flex items-center gap-3">
            <Button type="button" onClick={onCancel} variant="secondary" className="flex-1" disabled={confirmando}>
              {cancelText}
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              variant={estilo.botaoConfirmar}
              className="flex-1"
              loading={confirmando}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
